import { useState } from 'react'
import { useProcessesData } from './useProcessesData'
import { LeftPanel } from './SidePanels/LeftPanel'
import { RightPanel } from './SidePanels/RightPanel/RightPanel'
import { ScoopSpinner, ScoopCenter } from '../../../ui'
import { useModal, MODALS } from '../../../modals'
import { ProcessNoDataInput } from './ProcessNoDataInput/ProcessNoDataInput'
import { ProcessNoDatasetFound } from './ProcessNoDatasetFound/ProcessNoDatasetFound'
import { ProcessDiagram } from './ProcessDiagram'
import { ProcessError } from './ProcessError/ProcessError'
import styles from './Process.module.scss'

export const Process = ({ embeddedDiagram, processDiagramToEdit }) => {
    const textInputModal = useModal(MODALS.COMMON.TEXT_INPUT)
    const deleteModal = useModal(MODALS.COMMON.DELETE)
    const loadDiagramModal = useModal(MODALS.PROCESS.LOAD_DIAGRAM)

    const {
        isEmptyData,
        isEmptyDataset,
        preliminaryDataLoading,
        isLoadingHook,
        isError,
        isRenderedInACanvas,
        diagramState,
        handleLoadDiagram,
        datasetToAnalyzeForm,
        onChangeDatasetToAnalyzeForm,
        datasetDropdownOptions,
        datasetDropdownLoading,
        columnDropdownOptions,
        valueDropdownOptions,
        onToggleShowNodes,
        displayLinksAboveThreshold,
        diagramSetBackgroundColor,
        diagramSetNodeColor,
        diagramSetIsSankey,
        getOptionWithStyles,
        getSankeyTheme,
        handleFetchSankey,
        diagramSetSankeyNumberOfSteps,
        diagramSetSankeyPeriod,
        diagramSetSankeyIsStepBased,
        diagramSetAllowedStartingValues,
        diagramSetFilterV2,
        generateOptionsDropdownSankeyStartingValues,
        startingValuesOptions,
        diagramSetPathColor,
        handleRequestDiagram,
        diagramSetFilterSnapshotDate,
        diagramApplyFiltersAndSnapshotDate,
        preliminaryData,
        diagramSetStep,
        handleSave,
        handleDeleteDiagram,

        //Theme Select
        themeDropdownOptions,
        diagramSetTheme,

        pointerEvents,

        //Date setters
        diagramSetStartDate,
        diagramSetEndDate,
    } = useProcessesData(embeddedDiagram, processDiagramToEdit)

    const [isOpenLeftPanel, setIsOpenLeftPanel] = useState(true)
    const [isOpenRightPanel, setIsOpenRightPanel] = useState(true)

    const handleSaveAsDialog = async () => {
        const result = await textInputModal.show({
            title: 'Save Diagram As',
            label: 'Diagram Name',
            confirmText: 'Save',
        })
        if (result?.value) {
            handleSave(result.value)
        }
    }

    const handleDeleteDialog = async () => {
        const confirmed = await deleteModal.show({
            title: 'Diagram',
            description: `Are you sure you want to delete "${diagramState.diagramName}"? This action cannot be undone.`,
        })
        if (confirmed) {
            handleDeleteDiagram()
        }
    }

    const handleOpenLoadDiagram = async () => {
        const result = await loadDiagramModal.show()
        if (result?.diagramName) {
            handleLoadDiagram(result.diagramName)
        }
    }

    const containerClassName =
        !isRenderedInACanvas && !isEmptyDataset ? styles.containerWithBackground : styles.container

    return (
        <div className={containerClassName}>
            {!isRenderedInACanvas && !isEmptyDataset && (
                <LeftPanel
                    //States
                    isOpen={isOpenLeftPanel}
                    setIsOpen={setIsOpenLeftPanel}
                    diagramState={diagramState}
                    diagramSetFilterV2={diagramSetFilterV2}
                    preliminaryData={preliminaryData}
                    datasetDropdownOptions={datasetDropdownOptions}
                    datasetDropdownLoading={datasetDropdownLoading}
                    columnDropdownOptions={columnDropdownOptions}
                    valueDropdownOptions={valueDropdownOptions}
                    diagramSetFilterSnapshotDate={diagramSetFilterSnapshotDate}
                    diagramApplyFiltersAndSnapshotDate={diagramApplyFiltersAndSnapshotDate}
                    diagramSetStartDate={diagramSetStartDate}
                    diagramSetEndDate={diagramSetEndDate}
                    // Functions
                    handleRequestDiagram={handleRequestDiagram}
                    handleFetchSankey={handleFetchSankey}
                    onChangeDatasetToAnalyzeForm={onChangeDatasetToAnalyzeForm}
                    onOpenLoadDiagram={handleOpenLoadDiagram}
                    datasetToAnalyzeForm={datasetToAnalyzeForm}
                    handleSave={handleSave}
                    onSaveAs={handleSaveAsDialog}
                    onDelete={handleDeleteDialog}
                />
            )}
            {!isRenderedInACanvas && !isEmptyDataset && (
                <RightPanel
                    //States
                    isOpen={isOpenRightPanel}
                    setIsOpen={setIsOpenRightPanel}
                    diagramState={diagramState}
                    themeDropdownOptions={themeDropdownOptions}
                    // Functions
                    handleFetchSankey={handleFetchSankey}
                    onToggleShowNodes={onToggleShowNodes}
                    displayLinksAboveThreshold={displayLinksAboveThreshold}
                    generateOptionsDropdownSankeyStartingValues={
                        generateOptionsDropdownSankeyStartingValues
                    }
                    startingValuesOptions={startingValuesOptions}
                    //Setters fn
                    diagramSetBackgroundColor={diagramSetBackgroundColor}
                    diagramSetNodeColor={diagramSetNodeColor}
                    diagramSetIsSankey={diagramSetIsSankey}
                    diagramSetSankeyNumberOfSteps={diagramSetSankeyNumberOfSteps}
                    diagramSetSankeyPeriod={diagramSetSankeyPeriod}
                    diagramSetSankeyIsStepBased={diagramSetSankeyIsStepBased}
                    diagramSetAllowedStartingValues={diagramSetAllowedStartingValues}
                    diagramSetPathColor={diagramSetPathColor}
                    diagramSetStep={diagramSetStep}
                    diagramSetTheme={diagramSetTheme}
                />
            )}

            {isError && <ProcessError diagramName={diagramState?.diagramName} />}

            {/* No has Inboxes or Dataset*/}
            {isEmptyDataset && !preliminaryDataLoading && <ProcessNoDatasetFound />}

            {/* No has Loaded any Diagram  */}
            {!isLoadingHook && isEmptyData && <ProcessNoDataInput />}

            {/* Loading State */}
            {isLoadingHook && (
                <ScoopCenter className={styles.loadingCenter}>
                    <ScoopSpinner size={42} />
                </ScoopCenter>
            )}

            {/* Diagrams */}
            {!isEmptyDataset && !isEmptyData && !isLoadingHook && !isError && (
                <ProcessDiagram
                    diagramState={diagramState}
                    getOptionWithStyles={getOptionWithStyles}
                    getSankeyTheme={getSankeyTheme}
                    isOpenRightPanel={isOpenRightPanel}
                    isOpenLeftPanel={isOpenLeftPanel}
                    isRenderedInACanvas={isRenderedInACanvas}
                    pointerEvents={pointerEvents}
                />
            )}
        </div>
    )
}
